import React, { useState, useEffect } from "react";
import dynamic from "next/dynamic";
const Link = dynamic(() => import("next/link"), { ssr: true });

const pipelineSteps = [
  {
    label: "Bank transactions imported",
    value: "1,247 transactions",
    type: "done",
    tag: "AI",
  },
  {
    label: "Auto-categorised & reconciled",
    value: "99.2% accuracy",
    type: "done",
    tag: "AI",
  },
  {
    label: "Expert review in progress",
    value: "Sarah is reviewing...",
    type: "human",
    tag: "Human",
  },
  {
    label: "BAS report generation",
    value: "Queued",
    type: "ai",
    tag: "AI",
  },
  {
    label: "Final sign-off & lodgement",
    value: "Pending review",
    type: "human",
    tag: "Human",
  },
];

const PipelineIcon = ({ type }) => {
  const base =
    "w-9 h-9 rounded-[10px] flex items-center justify-center text-sm flex-shrink-0";
  if (type === "done")
    return (
      <div className={`${base} bg-[rgba(34,197,94,0.1)]`}>
        <svg className="w-4 h-4 text-[#22C55E]" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      </div>
    );
  if (type === "human")
    return (
      <div className={`${base} bg-[rgba(238,76,78,0.1)]`}>
        <svg className="w-4 h-4 text-textNoConnection" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
        </svg>
      </div>
    );
  return (
    <div className={`${base} bg-blue-primary-500/10`}>
      <svg className="w-4 h-4 text-blue-primary-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
      </svg>
    </div>
  );
};

const TagPill = ({ tag }) => {
  const colors = {
    AI: "bg-blue-primary-500/10 text-blue-primary-500",
    Human: "bg-textNoConnection/[0.08] text-textNoConnection",
  };
  return (
    <span
      className={`text-[10px] px-[10px] py-[3px] rounded-full font-bold uppercase tracking-[0.5px] ml-auto flex-shrink-0 ${colors[tag] || colors.AI}`}
    >
      {tag}
    </span>
  );
};

export default function NewHero({ homeRef }) {
  const [activeStep, setActiveStep] = useState(2);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % pipelineSteps.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative bg-gradient-to-br from-[#EFF6FF] via-[#E3EEFF] to-white overflow-hidden min-h-[92vh] flex items-center">
      {/* Background gradient orbs */}
      <div className="absolute -top-[200px] -right-[200px] w-[800px] h-[800px] rounded-full bg-[radial-gradient(circle,rgba(0,113,184,0.08)_0%,transparent_70%)]" />
      <div className="absolute -bottom-[150px] -left-[100px] w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(0,113,184,0.05)_0%,transparent_70%)]" />

      <div className="relative z-10 px-6 lg:px-8 pt-[140px] pb-[80px] lg:pt-[160px] lg:pb-[100px] mx-auto max-w-7xl w-full">
        <div
          ref={homeRef}
          className="w-full grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-8 lg:gap-20 items-center"
        >
          {/* Left: Copy */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 bg-blue-primary-500/10 text-blue-primary-500 text-[13px] font-semibold px-4 py-1.5 rounded-full mb-7 tracking-[0.5px]">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
              </svg>
              AI-Powered &middot; Human-Verified
            </div>
            <h1 className="font-inter text-[42px] sm:text-[52px] lg:text-[60px] xl:text-[68px] font-light leading-[1.08] tracking-[-2px] mb-7 text-[#0F1D2F]">
              <strong className="font-extrabold">AI automation</strong> meets{" "}
              <strong className="font-extrabold text-textNoConnection">
                real accountants
              </strong>{" "}
              you can trust
            </h1>
            <p className="text-[17px] sm:text-[19px] leading-[1.7] text-gray-cool-500 max-w-[540px] mx-auto lg:mx-0 mb-5">
              Our AI agents handle the heavy lifting to prepare closing the
              books — while our CAs and CPAs review, verify, and ensure
              everything is audit-ready.
            </p>
            <div className="w-20 h-[3px] bg-blue-primary-500 rounded-sm my-8 mx-auto lg:mx-0" />
            <div className="flex gap-4 flex-wrap mt-2 justify-center lg:justify-start">
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 bg-blue-primary-500 hover:bg-[#005bb5] text-white px-9 py-4 rounded-full text-base font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(0,116,230,0.35)]"
              >
                Book a Demo
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center bg-white hover:bg-gray-cool-50 text-[#0F1D2F] border border-gray-cool-200 px-9 py-4 rounded-full text-base font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)]"
              >
                Get Started
              </Link>
            </div>
          </div>

          {/* Right: Live Workflow Card - Desktop */}
          <div className="relative hidden lg:block" style={{ padding: "30px 0" }}>
            <div className="bg-[rgba(0,113,184,0.04)] backdrop-blur-[20px] border border-[rgba(0,113,184,0.1)] rounded-[20px] p-9 relative">
              {/* Card header */}
              <div className="flex justify-between items-center mb-7">
                <span className="text-[14px] font-semibold text-gray-cool-500 uppercase tracking-[1px]">
                  Live Workflow
                </span>
                <span className="bg-blue-primary-500/10 text-blue-primary-500 text-[12px] px-3 py-1 rounded-[20px] font-semibold inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-[#22C55E] rounded-full animate-pulse" />
                  Processing
                </span>
              </div>
              {/* Pipeline */}
              <div className="flex flex-col gap-3.5">
                {pipelineSteps.map((step, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-3.5 px-[18px] py-3.5 rounded-xl border transition-all duration-300 ${
                      i === activeStep
                        ? "bg-[rgba(0,113,184,0.06)] border-[rgba(0,113,184,0.2)]"
                        : "bg-white border-[rgba(0,0,0,0.06)]"
                    }`}
                  >
                    <PipelineIcon type={step.type} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] text-gray-cool-500">
                        {step.label}
                      </div>
                      <div className="text-[14px] font-semibold text-gray-cool-900">
                        {step.value}
                      </div>
                    </div>
                    <TagPill tag={step.tag} />
                  </div>
                ))}
              </div>
            </div>
            {/* Floating stat cards with bob animation */}
            <div className="absolute -top-[16px] right-[24px] p-4 px-[22px] rounded-[14px] bg-white border border-[rgba(0,0,0,0.06)] shadow-[0_8px_32px_rgba(0,0,0,0.1)] z-10 animate-float-bob">
              <div className="flex items-center gap-3">
                <span className="text-[28px] leading-none">🤖</span>
                <div className="flex flex-col">
                  <span className="text-[12px] text-gray-cool-500 font-medium leading-tight">AI Speed</span>
                  <span className="text-[18px] font-bold text-gray-cool-900 leading-tight">&lt; 2 min</span>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-[16px] left-[24px] p-4 px-[22px] rounded-[14px] bg-white border border-[rgba(0,0,0,0.06)] shadow-[0_8px_32px_rgba(0,0,0,0.1)] z-10 animate-float-bob" style={{ animationDelay: "1.5s" }}>
              <div className="flex items-center gap-3">
                <span className="text-[28px] leading-none">👩‍💼</span>
                <div className="flex flex-col">
                  <span className="text-[12px] text-gray-cool-500 font-medium leading-tight">Human Verified</span>
                  <span className="text-[18px] font-bold text-gray-cool-900 leading-tight">100%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile: simplified workflow card */}
          <div className="relative lg:hidden">
            <div className="bg-[rgba(0,113,184,0.04)] border border-[rgba(0,113,184,0.1)] rounded-[20px] p-6">
              <div className="flex justify-between items-center mb-5">
                <span className="text-[13px] font-semibold text-gray-cool-500 uppercase tracking-[1px]">
                  Live Workflow
                </span>
                <span className="bg-blue-primary-500/10 text-blue-primary-500 text-[11px] px-3 py-1 rounded-[20px] font-semibold inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-[#22C55E] rounded-full animate-pulse" />
                  Processing
                </span>
              </div>
              <div className="flex flex-col gap-3">
                {pipelineSteps.slice(0, 3).map((step, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-300 ${
                      i === activeStep && activeStep < 3
                        ? "bg-[rgba(0,113,184,0.06)] border-[rgba(0,113,184,0.2)]"
                        : "bg-white border-[rgba(0,0,0,0.06)]"
                    }`}
                  >
                    <PipelineIcon type={step.type} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[12px] text-gray-cool-500">
                        {step.label}
                      </div>
                      <div className="text-[13px] font-semibold text-gray-cool-900">
                        {step.value}
                      </div>
                    </div>
                    <TagPill tag={step.tag} />
                  </div>
                ))}
              </div>
              {/* Inline stat row on mobile */}
              <div className="flex gap-4 mt-4 pt-4 border-t border-[rgba(0,0,0,0.06)]">
                <div className="flex-1 text-center">
                  <div className="text-[16px]">🤖</div>
                  <div className="text-[11px] text-gray-cool-500">AI Speed</div>
                  <div className="text-[15px] font-bold text-gray-cool-900">
                    &lt; 2 min
                  </div>
                </div>
                <div className="flex-1 text-center">
                  <div className="text-[16px]">👩‍💼</div>
                  <div className="text-[11px] text-gray-cool-500">Human Verified</div>
                  <div className="text-[15px] font-bold text-gray-cool-900">100%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
